import { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { InspectionContext } from "../types";
import { getStreamValue } from "../utils";
import Select from "@/components/atoms/Select";
import Text from "@/components/atoms/Text";
import WeightInput from "@/components/molecules/WeightInput";
import { AddRowButton, HeaderText, PhotoLine, RemoveRowButton } from "./FormParts";
import { InspectionFormApi, InspectionSummary, toStreamKg } from "../useInspectionForm";
import { useInspectionFormat } from "../useInspectionFormat";
import { InspectionErrors, MAX_ADDITIONAL_STREAMS, MAX_PHOTOS_PER_STREAM } from "../validation";
import styles from "../styles.module.css";

const COLUMNS = "minmax(0, 1fr) 110px 190px 100px 36px";
/** On a phone the contract and the weight get a line each, above the price, cost and remove button. */
const NARROW_COLUMNS = "auto minmax(0, 1fr) 36px";

/** The column is narrow; the options ("#181 Contract name - product") get room to stay on one line. */
const WIDE_DROPDOWN = { width: 360, position: "bottom-start" } as const;

interface FractionsTableProps {
  form: InspectionFormApi;
  summary: InspectionSummary;
  context: InspectionContext;
  errors: InspectionErrors;
}

/**
 * The fractions received on one delivery. Row 1 is the original order; each extra row picks one of the
 * active contracts between the same two parties, and becomes a new order on that contract when submitted.
 * Laid out like the deviation tables: the contract's price, the weight, and what the weight costs at that price.
 */
export default function FractionsTable({ form, summary, context, errors }: FractionsTableProps) {
  const { t } = useTranslation();
  const format = useInspectionFormat();
  const { streams } = form.values;
  const error = (path: string) => (errors[path] ? t(errors[path]) : undefined);

  const chosenContractIds = streams.map((stream) => stream.contractId);
  // Each contract once: a fraction on two contracts is offered twice, once per contract.
  const contractOptions = (current: number | null) =>
    context.eligibleContracts
      .filter((contract) => contract.contractId === current || !chosenContractIds.includes(contract.contractId))
      .map((contract) => ({ value: String(contract.contractId), label: summary.labelFor(contract) }));
  const canAddFraction =
    streams.length <= MAX_ADDITIONAL_STREAMS &&
    context.eligibleContracts.some((contract) => !chosenContractIds.includes(contract.contractId));

  return (
    <div
      className={styles.lineTable}
      style={{ "--line-columns": COLUMNS, "--line-columns-narrow": NARROW_COLUMNS } as CSSProperties}
    >
      <div className={`${styles.lineGrid} ${styles.lineHeader}`}>
        <HeaderText>{t("inspections:fractions.fraction")}</HeaderText>
        <HeaderText>{t("inspections:fractions.price")}</HeaderText>
        <HeaderText>{t("inspections:fractions.weight")}</HeaderText>
        <HeaderText className={styles.alignRight}>{t("inspections:fractions.cost")}</HeaderText>
        <span />
      </div>

      {streams.map((stream, index) => {
        const contract = summary.contractFor(stream.contractId);
        const weightKg = toStreamKg(stream);
        // Nothing to price until the row has a contract and a weight, or when its unit can't be converted to kg.
        const cost = contract && weightKg > 0 ? getStreamValue(weightKg, contract) : null;

        return (
          <div key={stream.key} className={styles.lineRow} data-testid={`inspection-fraction-${index}`}>
            <div className={`${styles.lineGrid} ${styles.fractionGrid}`}>
              {index === 0 ? (
                <Text truncate nowrap className={`${styles.lineWide} ${styles.fractionCell}`}>
                  {summary.streamLabel(stream.key)}
                </Text>
              ) : (
                <Select
                  className={`${styles.lineWide} ${styles.ellipsis}`}
                  aria-label={t("inspections:fractions.fraction")}
                  placeholder={t("inspections:fractions.selectContract")}
                  data={contractOptions(stream.contractId)}
                  comboboxProps={WIDE_DROPDOWN}
                  value={stream.contractId === null ? null : String(stream.contractId)}
                  onChange={(value) => form.updateStream(index, { contractId: value ? Number(value) : null })}
                  error={error(`streams.${index}.contractId`)}
                  data-testid={`inspection-fraction-${index}-contract`}
                />
              )}
              <Text
                size="sm"
                className={styles.fractionCell}
                data-label={t("inspections:fractions.price")}
                data-testid={`inspection-fraction-${index}-price`}
              >
                {contract?.price != null && contract.priceUnit
                  ? format.unitPrice(contract.price, contract.currency, contract.priceUnit.unit)
                  : "—"}
              </Text>
              <div className={styles.fractionWeight}>
                <WeightInput
                  aria-label={t("inspections:fractions.weight")}
                  value={stream.weight}
                  onChange={(weight) => form.updateStream(index, { weight })}
                  error={error(`streams.${index}.weight`)}
                  data-testid={`inspection-fraction-${index}-weight`}
                />
              </div>
              <Text
                size="sm"
                className={`${styles.alignRight} ${styles.fractionCell}`}
                data-label={t("inspections:fractions.cost")}
                data-testid={`inspection-fraction-${index}-cost`}
              >
                {contract && cost !== null ? format.amount(cost, contract.currency) : "—"}
              </Text>
              {index === 0 ? (
                <span />
              ) : (
                <div className={styles.fractionCell}>
                  <RemoveRowButton onClick={() => form.removeStream(index)} label={t("inspections:fractions.remove")} />
                </div>
              )}
            </div>
            <PhotoLine
              photos={stream.photos}
              onChange={(photos) => form.updateStream(index, { photos })}
              max={MAX_PHOTOS_PER_STREAM}
              error={error(`streams.${index}.photos`)}
              data-testid={`inspection-fraction-${index}-photos`}
            />
          </div>
        );
      })}

      <div className={styles.lineFooter}>
        <AddRowButton onClick={form.addStream} disabled={!canAddFraction} data-testid="inspection-add-fraction">
          {t("inspections:fractions.add")}
        </AddRowButton>
        <div className={styles.lineTotals}>
          <Text size="sm" data-testid="inspection-fractions-weight-sum">
            {t("inspections:fractions.sum", { weight: format.number(summary.totalWeightKg) })}
          </Text>
          {summary.streamsTotal && (
            <Text size="sm" data-testid="inspection-fractions-cost-sum">
              {format.amount(summary.streamsTotal.amount, summary.streamsTotal.currency)}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
}
